import { GameBase } from '../../shared/models/game-base';
import type { ITeam } from '../../shared/interfaces/ITeam';
import { SelectionType } from '../../shared/enums/selection-type';
import { DateTime } from 'luxon';
import { formatLongDate } from '../../shared/date-helpers';

export class NflGame extends GameBase {
  constructor(
    home: KnockoutObservable<ITeam> | ITeam,
    away: KnockoutObservable<ITeam> | ITeam,
    week: number,
    primeTimeType?: number,
    gameDate?: string,
    gameKey?: string,
    gameGuid?: string,
    gameScheduleNum?: number
  ) {
    super({
      home: home as KnockoutObservable<ITeam>,
      away: away as KnockoutObservable<ITeam>,
      week,
      primeTimeNum: primeTimeType,
      gameDate,
      gameKey,
      gameGuid,
      gameScheduleNum,
    });

    // Re-assign NFL-specific computed overrides after super() completes.
    // The base class assigns these in its constructor; we replace them here.
    const self = this;

    // gameDateShortDisplay: hour==13 → empty string
    self.gameDateShortDisplay = ko.pureComputed(() => {
      if (self.gameDate()) {
        const hour = self.gameDate().hour;
        if (hour === 13) {
          return '';
        }
        return self.gameDate().toFormat('ccc').slice(0, 2);
      }
      return '';
    });

    // gameDateLongDisplay: uses "EST" suffix
    self.gameDateLongDisplay = ko.pureComputed(() => {
      if (self.gameDate()) {
        return formatLongDate(self.gameDate()) + ' EST';
      }
      return '';
    });
  }

  protected override createGameClass(): string {
    const self = this;
    let retStr = '';
    if (self.selectionMade()) {
      if (self.selectionType() !== SelectionType.TIE && self.selectionType() !== SelectionType.NONE) {
        if (self.TeamThatWon()) {
          retStr += 'selection-made ';
          retStr += self.TeamThatWon()().TeamAbbreviation;
        }
      } else if (self.selectionType() === SelectionType.TIE) {
        retStr += 'selection-made ';
        retStr += 'TIE';
      }
    }
    return retStr;
  }

  protected override createTeamThatWon(): KnockoutObservable<ITeam> | null {
    const self = this;
    if (
      self.selectionMade() &&
      self.selectionType() !== SelectionType.NONE &&
      self.selectionType() !== SelectionType.TIE
    ) {
      switch (self.selectionType()) {
        case SelectionType.AWAY_WIN:
          return self.away;
        case SelectionType.HOME_WIN:
          return self.home;
        default:
          return null;
      }
    }
    return null;
  }

  protected override createTeamThatLost(): KnockoutObservable<ITeam> | null {
    const self = this;
    if (
      self.selectionMade() &&
      self.selectionType() !== SelectionType.NONE &&
      self.selectionType() !== SelectionType.TIE
    ) {
      switch (self.selectionType()) {
        case SelectionType.AWAY_WIN:
          return self.home;
        case SelectionType.HOME_WIN:
          return self.away;
        default:
          return null;
      }
    }
    return null;
  }

  protected override createSortOrder(): number {
    const self = this;

    // Both gameKey and gameDate present: use gameDate with day-of-week weighting
    if (self.gameKey && self.gameDate()) {
      const date = self.gameDate();
      const dayOfWeek = date.weekday;
      const sort = date.toMillis();
      if (dayOfWeek === 1) {
        return sort * 10000; // Mon
      } else if (dayOfWeek === 4) {
        return sort / 100000; // Thurs
      } else if (dayOfWeek === 6) {
        return sort; // Sat
      }
      return sort;
    }

    // gameKey only (no date)
    if (self.gameKey) {
      return self.gameKey as number;
    }

    // gameDate only (no gameKey)
    if (self.gameDate() && DateTime.isDateTime(self.gameDate())) {
      const date = self.gameDate();
      const dayOfWeek = date.weekday;
      const sort = date.toMillis();
      if (dayOfWeek === 1) {
        return sort * 10000; // Mon
      } else if (dayOfWeek === 4) {
        return sort / 100000; // Thurs
      } else if (dayOfWeek === 6) {
        return sort; // Sat
      }
      return sort;
    }

    // primeTimeNum fallback
    switch (self.primeTimeNum()) {
      case 1: // sunday night
        return 7;
      case 2: // saturday
        return 9;
      case 3: // Mon
        return 1;
      case 4: // Thurs
        return 10;
      case 5: // MX
        return 6;
      case 6: // LN
        return 6;
      default:
        return 8;
    }
  }
}
